import { useUserReportPreferences } from '@ltvco/refresh-lib/hooks';
import { useSession } from '@ltvco/refresh-lib/v1';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { PropertyReportSectionView } from 'hooks/usePropertyReportSectionOrder';

/** Value stored on account.user_info.signup_reasons for professional property use. */
export const PROFESSIONAL_SIGNUP_REASON =
  'propf_real_estate_investor_professional';

export const hasProfessionalSignupReason = (
  signupReasons: ReadonlyArray<string | number> | null | undefined
): boolean => {
  if (!Array.isArray(signupReasons)) {
    return false;
  }

  return signupReasons.some(
    (reason) =>
      String(reason).trim().toLowerCase() === PROFESSIONAL_SIGNUP_REASON
  );
};

type ReportPersona = 'homeowner' | 'realtor';

type AccountPreferencesWithPersonas = {
  user_report_preferences?: {
    personas?: Record<string, string>;
  };
  user_info?: {
    signup_reasons?: string[] | null;
  };
};

const isReportPersona = (value: string | undefined): value is ReportPersona =>
  value === 'homeowner' || value === 'realtor';

const readAccountPersona = (
  account: AccountPreferencesWithPersonas | null | undefined
): ReportPersona | undefined => {
  const value = account?.user_report_preferences?.personas?.property_report;

  return isReportPersona(value) ? value : undefined;
};

const personaForView = (view: PropertyReportSectionView): ReportPersona =>
  view === 'professional' ? 'realtor' : 'homeowner';

const viewForPersona = (persona: ReportPersona): PropertyReportSectionView =>
  persona === 'realtor' ? 'professional' : 'default';

export const resolvePropertyReportView = ({
  storedPersona,
  signupReasons,
}: {
  storedPersona?: ReportPersona;
  signupReasons?: ReadonlyArray<string | number> | null;
}): PropertyReportSectionView => {
  if (storedPersona) {
    return viewForPersona(storedPersona);
  }

  if (hasProfessionalSignupReason(signupReasons)) {
    return 'professional';
  }

  return 'default';
};

export interface UsePropertyReportViewArgs {
  /**
   * When true (UPS-1266 on), persist a professional signup default so the
   * next visit reads the saved realtor persona instead of re-deriving it.
   */
  persistProfessionalSignupDefault?: boolean;
}

export interface UsePropertyReportViewResult {
  view: PropertyReportSectionView;
  setView: (nextView: PropertyReportSectionView) => void;
  isIntroVisible: boolean;
  dismissIntro: () => void;
}

export const usePropertyReportView = ({
  persistProfessionalSignupDefault = false,
}: UsePropertyReportViewArgs = {}): UsePropertyReportViewResult => {
  const { session } = useSession();
  const { updatePreferences } = useUserReportPreferences();
  const [view, setViewState] = useState<PropertyReportSectionView>('default');
  const [hasSavedPersona, setHasSavedPersona] = useState(false);
  const hasPersistedSignupDefault = useRef(false);

  const account = session.account?.account as
    | AccountPreferencesWithPersonas
    | undefined;
  const accountLoaded = session.account != null;
  const storedPersona = readAccountPersona(account);
  const hasPersona = storedPersona != null;
  const signupReasons = account?.user_info?.signup_reasons;
  const resolvedView = resolvePropertyReportView({
    storedPersona,
    signupReasons,
  });

  useEffect(() => {
    // Keep an in-flight explicit choice until the account persona refreshes.
    if (hasSavedPersona && !storedPersona) {
      return;
    }

    setViewState(resolvedView);
  }, [hasSavedPersona, resolvedView, storedPersona]);

  const persistPersona = updatePreferences.mutate;

  useEffect(() => {
    if (
      !persistProfessionalSignupDefault ||
      !accountLoaded ||
      hasPersona ||
      hasSavedPersona ||
      hasPersistedSignupDefault.current ||
      resolvedView !== 'professional'
    ) {
      return;
    }

    hasPersistedSignupDefault.current = true;
    persistPersona(
      {
        user_report_preferences: {
          property_report: {
            persona: 'realtor',
          },
        },
      },
      {
        onSuccess: () => {
          setHasSavedPersona(true);
        },
        onError: () => {
          hasPersistedSignupDefault.current = false;
        },
      }
    );
  }, [
    accountLoaded,
    hasPersona,
    hasSavedPersona,
    persistPersona,
    persistProfessionalSignupDefault,
    resolvedView,
  ]);

  const setView = useCallback(
    (nextView: PropertyReportSectionView) => {
      const previousView = view;
      setViewState(nextView);
      persistPersona(
        {
          user_report_preferences: {
            property_report: {
              persona: personaForView(nextView),
            },
          },
        },
        {
          onSuccess: () => {
            setHasSavedPersona(true);
          },
          onError: () => {
            setViewState(previousView);
          },
        }
      );
    },
    [persistPersona, view]
  );

  const dismissIntro = useCallback(() => {
    setView(view);
  }, [setView, view]);

  return {
    view,
    setView,
    isIntroVisible: accountLoaded && !hasPersona && !hasSavedPersona,
    dismissIntro,
  };
};
